/**
 * Templates written in the rich-text editor that hold slots for other pieces of
 * rich text — the Shiurei Torah template's {content} and {content1}–{content3}.
 *
 * The slot cannot simply be swapped for the content's HTML. The editor writes a
 * slot inside the formatting it was given,
 *
 *   <p style="text-align: center"><span style="font-family: FBVilna, sans-serif; font-size: 40px">{content}</span></p>
 *
 * and the content as paragraphs of its own. A <p> cannot sit inside another, so
 * the browser closes the template's paragraph — and its font, size and colour
 * with it — right where the content begins: the content showed in the default
 * font.
 *
 * fillRichSlot puts the content in the way the editor would have, had it been
 * typed at the slot: each of its paragraphs takes the place of the template's
 * paragraph, with that paragraph's alignment and direction, and holds its text
 * inside the template's formatting. Whatever the content formats for itself
 * sits inside that, so it wins — as a format applied inside another does.
 *
 * readRichSlotFormat reads the same formatting for the editor
 * (Rich_Text_Editor's `slotFormat`), so the content is written looking the way
 * the screen will show it. Both walk the template the same way — the block the
 * slot is in, the formatting around it inside that block, and the blocks
 * around that — so the two cannot drift apart.
 */

// A slot stands in the template as its name between two private-use
// characters, so no template, title or date can contain one by chance.
const MARKER_OPEN = '\uE000';
const MARKER_CLOSE = '\uE001';
const MARKER_CHARACTERS = /[\uE000\uE001]/g;
const MARKER_PATTERN = /\uE000([^\uE000\uE001]*)\uE001/;
const EVERY_MARKER = /\uE000[^\uE000\uE001]*\uE001/g;

/** Text going into a template, without anything that could pose as a slot. */
export const withoutSlotMarker = (value: unknown): string =>
  String(value ?? '').replace(MARKER_CHARACTERS, '');

/** What stands in a template for the named slot until its content is put in. */
export const richSlotMarker = (name: string): string => `${MARKER_OPEN}${withoutSlotMarker(name)}${MARKER_CLOSE}`;

/** The marker of a template's one slot, for fillRichSlot / readRichSlotFormat. */
export const RICH_SLOT_MARKER = richSlotMarker('slot');

export type StyleMap = Record<string, string>;

/** The formatting a template gives its slot — what readRichSlotFormat returns. */
export interface RichSlotFormat {
  /**
   * The block the slot sits in — 'p', 'h1'…'h6' or 'li' — whose place each
   * paragraph of the content takes. null when the slot sits in a container (a
   * div, a quote, a table cell, or no block at all): the content then keeps its
   * own paragraphs, inside the container.
   */
  hostTag: string | null;
  /** That block's own style (its Quill classes read as the styles they stand for); each paragraph of the content starts from it. */
  blockStyle: StyleMap;
  /** That block's direction. */
  blockDir: string | null;
  /** The formatting around the slot inside its block — font, size, colour, bold… — applied to the content's text. */
  inlineStyle: StyleMap;
  /** What the content inherits from the blocks further out. */
  inheritedStyle: StyleMap;
  /** The direction those blocks set. */
  inheritedDir: string | null;
  /** The list the slot sits in, when it sits in a list item. */
  list: RichSlotList | null;
}

/** A slot inside a list item: how the display marks and indents the content. */
export interface RichSlotList {
  ordered: boolean;
  /**
   * Every paragraph of the content becomes an item of its own (the slot is in
   * the item itself, as Quill wrote lists); otherwise the content stays in the
   * one item, its first line beside the marker (the slot is in a paragraph
   * inside it, as Tiptap writes lists).
   */
  itemPerLine: boolean;
  /** Whether the first line has the item's marker beside it at all. */
  marker: boolean;
  /** The number the first item shows, in a numbered list. */
  start: number;
  /** Quill's right-to-left item: marker and indent on the right. */
  rtl: boolean;
}

/* --------------------------------- elements -------------------------------- */

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;
const FRAGMENT_NODE = 11;

// Block-level elements: a slot inside one of these is past the formatting
// around it.
const BLOCK_TAGS = new Set([
  'ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'BODY', 'CAPTION', 'CENTER', 'DD', 'DETAILS', 'DIALOG',
  'DIR', 'DIV', 'DL', 'DT', 'FIELDSET', 'FIGCAPTION', 'FIGURE', 'FOOTER', 'FORM', 'H1', 'H2', 'H3',
  'H4', 'H5', 'H6', 'HEADER', 'HGROUP', 'HR', 'LI', 'MAIN', 'MENU', 'NAV', 'OL', 'P', 'PRE',
  'SECTION', 'SUMMARY', 'TABLE', 'TBODY', 'TD', 'TFOOT', 'TH', 'THEAD', 'TR', 'UL',
]);
// A slot in one of these is a line of text: the content's paragraphs take its place.
const LINE_HOST = /^(P|H[1-6]|LI)$/;
// The content's own lines.
const LINE_BLOCK = /^(P|H[1-6])$/;
const HEADING = /^H[1-6]$/;
// Content blocks whose paragraphs are styled where they stand.
const TREE_BLOCK = /^(UL|OL|LI|BLOCKQUOTE|DIV)$/;
// Media the editors write as a block of their own (Tiptap's image, Quill's
// video): left exactly as they are, never pulled into a line.
const MEDIA_TAGS = new Set(['IMG', 'IFRAME', 'VIDEO', 'AUDIO', 'SVG', 'CANVAS', 'OBJECT', 'EMBED']);
// Content in its own right, even without any text.
const SUBSTANTIVE = 'img,br,hr,iframe,video,audio,svg,canvas,object,embed,input';
// HTML's whitespace — a no-break space is written on purpose, so it counts as text.
const BLANK = /^[ \t\n\r\f]*$/;

const ALIGN_CLASS = /^ql-align-/;
const DIRECTION_CLASS = /^ql-direction-/;
const INDENT_CLASS = /^ql-indent-/;

const tagOf = (node: Node): string => node.nodeName.toUpperCase();

const isElement = (node: Node | null): node is HTMLElement => Boolean(node && node.nodeType === ELEMENT_NODE);

const isBlock = (node: Node): boolean => isElement(node) && BLOCK_TAGS.has(tagOf(node));

const hasBlockChild = (el: Element): boolean => Array.from(el.childNodes).some(isBlock);

/** Whether a node — or a list of them — holds anything that shows. */
const hasSubstance = (input: Node | Node[] | null): boolean => {
  if (!input) return false;
  if (Array.isArray(input)) return input.some((node) => hasSubstance(node));
  if (input.nodeType === TEXT_NODE) return !BLANK.test(input.nodeValue || '');
  if (input.nodeType !== ELEMENT_NODE && input.nodeType !== FRAGMENT_NODE) return false;
  const parent = input as Element | DocumentFragment;
  if (isElement(input) && input.matches(SUBSTANTIVE)) return true;
  return !BLANK.test(parent.textContent || '') || Boolean(parent.querySelector(SUBSTANTIVE));
};

const classNames = (el: Element): Array<string> => Array.from(el.classList || []);

const removeClasses = (el: Element, pattern: RegExp): void => {
  classNames(el).forEach((name) => {
    if (pattern.test(name)) el.classList.remove(name);
  });
};

const hasOwnAlign = (el: HTMLElement): boolean =>
  Boolean(el.style.textAlign) || classNames(el).some((name) => ALIGN_CLASS.test(name));

const hasOwnDirection = (el: HTMLElement): boolean =>
  el.hasAttribute('dir') || Boolean(el.style.direction) || classNames(el).some((name) => DIRECTION_CLASS.test(name));

const dropEmptyAttributes = (el: Element): void => {
  if (el.getAttribute('style') === '') el.removeAttribute('style');
  if (el.getAttribute('class') === '') el.removeAttribute('class');
};

/* ------------------------------ the content ------------------------------ */

type Unit =
  // A paragraph or heading of the content (el), or text standing loose (el null).
  | { kind: 'line'; el: HTMLElement | null; nodes: Array<Node> }
  // A list, quote or division: its paragraphs are styled where they stand.
  | { kind: 'tree'; el: HTMLElement }
  // Kept exactly as written: code, a table, a rule, media.
  | { kind: 'as-is'; node: Node };

/** The content, block by block. */
const collectUnits = (parent: Node): Array<Unit> => {
  const units: Array<Unit> = [];
  let loose: Array<Node> = [];
  const closeLoose = () => {
    if (hasSubstance(loose)) units.push({ kind: 'line', el: null, nodes: loose });
    loose = [];
  };

  Array.from(parent.childNodes).forEach((node) => {
    if (node.nodeType === TEXT_NODE || (isElement(node) && !isBlock(node) && !MEDIA_TAGS.has(tagOf(node)))) {
      loose.push(node);
      return;
    }
    if (!isElement(node)) return;
    closeLoose();
    const tag = tagOf(node);
    if (LINE_BLOCK.test(tag) || (tag === 'DIV' && !hasBlockChild(node))) {
      units.push({ kind: 'line', el: node, nodes: Array.from(node.childNodes) });
    } else if (TREE_BLOCK.test(tag)) {
      units.push({ kind: 'tree', el: node });
    } else {
      units.push({ kind: 'as-is', node });
    }
  });
  closeLoose();
  return units;
};

/** The nodes, inside a copy of the formatting around the slot. */
const wrapInChain = (nodes: Array<Node>, chain: Array<Element>): Array<Node> => {
  if (!chain.length) return nodes;
  const copies = chain.map((el) => {
    const copy = el.cloneNode(false) as Element;
    copy.removeAttribute('id');
    return copy;
  });
  copies.reduce((outer, inner) => {
    outer.appendChild(inner);
    return inner;
  });
  const innermost = copies[copies.length - 1];
  nodes.forEach((node) => innermost.appendChild(node));
  return [copies[0]];
};

/**
 * The element's children, replaced. By hand rather than replaceChildren, which
 * the older Chromium in some TVs and players does not have — and this runs
 * inside the display's render, where a missing method blanks the screen.
 */
const setChildren = (el: Element, nodes: Array<Node>): void => {
  const fragment = el.ownerDocument.createDocumentFragment();
  nodes.forEach((node) => fragment.appendChild(node));
  while (el.firstChild) el.removeChild(el.firstChild);
  el.appendChild(fragment);
};

/**
 * A line of the content, its text inside the slot's formatting. An empty line
 * holds a line break in that formatting, so it is as tall as a line typed in
 * the template's font — as the editor draws it.
 */
const fillLine = (line: Element, chain: Array<Element>): void => {
  const children = Array.from(line.childNodes);
  if (hasSubstance(children)) {
    if (chain.length) setChildren(line, wrapInChain(children, chain));
    return;
  }
  // An empty paragraph already keeps its line (App.vue's `p:empty`); a heading
  // or anything else needs the break.
  if (chain.length || tagOf(line) !== 'P') {
    setChildren(line, wrapInChain([line.ownerDocument.createElement('br')], chain));
  }
};

/** Loose text inside a list item or a quote, wrapped where it stands. */
const wrapInPlace = (nodes: Array<Node>, chain: Array<Element>): void => {
  if (!chain.length || !nodes.length) return;
  const parent = nodes[0].parentNode;
  if (!parent) return;
  const next = nodes[nodes.length - 1].nextSibling;
  const [wrapper] = wrapInChain(nodes, chain);
  parent.insertBefore(wrapper, next);
};

/**
 * The slot block's alignment, direction and line spacing, on a block of the
 * content that sets none of its own.
 */
const inheritBlockDefaults = (el: HTMLElement, host: HTMLElement | null): void => {
  if (!host) return;
  if (!hasOwnAlign(el)) {
    if (host.style.textAlign) {
      el.style.textAlign = host.style.textAlign;
    } else {
      classNames(host).filter((name) => ALIGN_CLASS.test(name)).forEach((name) => el.classList.add(name));
    }
  }
  if (!hasOwnDirection(el)) {
    const dir = host.getAttribute('dir');
    if (dir) el.setAttribute('dir', dir);
    classNames(host).filter((name) => DIRECTION_CLASS.test(name)).forEach((name) => el.classList.add(name));
    if (host.style.direction) el.style.direction = host.style.direction;
  }
  if (!el.style.lineHeight && host.style.lineHeight) el.style.lineHeight = host.style.lineHeight;
};

/** A list, quote or division of the content: every paragraph in it, styled in place. */
const styleTree = (container: HTMLElement, host: HTMLElement | null, chain: Array<Element>): void => {
  const tag = tagOf(container);
  if (tag === 'UL' || tag === 'OL') {
    Array.from(container.children).forEach((child) => {
      if (isElement(child) && tagOf(child) === 'LI') styleTree(child, host, chain);
    });
    return;
  }

  let hasLooseText = false;
  collectUnits(container).forEach((unit) => {
    if (unit.kind === 'line') {
      if (unit.el) {
        inheritBlockDefaults(unit.el, host);
        fillLine(unit.el, chain);
      } else {
        hasLooseText = true;
        wrapInPlace(unit.nodes, chain);
      }
    } else if (unit.kind === 'tree') {
      styleTree(unit.el, host, chain);
    }
  });
  if (hasLooseText) inheritBlockDefaults(container, host);

  // The display draws a list item's first paragraph inline (Display.vue:
  // `li > p:first-child`), so the item — not the paragraph — sets where its
  // text sits: it takes the paragraph's alignment and direction, as the editor
  // shows them.
  const first = container.firstElementChild;
  if (tag === 'LI' && isElement(first) && LINE_BLOCK.test(tagOf(first))) liftToItem(container, first);
  if (tag === 'LI') markRtlItem(container);
};

/**
 * A right-to-left list item in Quill's own form: its class, which moves the
 * item's marker and indent to the right (quill.core.css keys them on
 * `.ql-direction-rtl`). By `dir` alone the item would keep the left-hand
 * geometry, and its marker would sit over the first letters.
 */
const markRtlItem = (item: HTMLElement): void => {
  if (item.getAttribute('dir') === 'rtl' || item.style.direction === 'rtl') item.classList.add('ql-direction-rtl');
};

/**
 * A list item's paragraph's alignment, direction and line spacing, on the
 * item. Written as a style and a `dir` — never Quill's classes, whose
 * right-to-left rule moves the item's indent to the other side, which the
 * editor does not do.
 */
const liftToItem = (item: HTMLElement, line: HTMLElement): void => {
  if (!hasOwnAlign(item)) {
    const alignClass = classNames(line).map((name) => /^ql-align-(.+)$/.exec(name)).find(Boolean);
    const align = line.style.textAlign || (alignClass ? alignClass[1] : '');
    if (align) item.style.textAlign = align;
  }
  if (!hasOwnDirection(item)) {
    const dir = line.getAttribute('dir') || (line.classList.contains('ql-direction-rtl') ? 'rtl' : '') || line.style.direction;
    if (dir) item.setAttribute('dir', dir);
    markRtlItem(item);
  }
  if (!item.style.lineHeight && line.style.lineHeight) item.style.lineHeight = line.style.lineHeight;
};

/** The element under another tag, attributes and children and all. */
const retag = (el: HTMLElement, tag: string): HTMLElement => {
  const copy = el.ownerDocument.createElement(tag.toLowerCase());
  Array.from(el.attributes).forEach(({ name, value }) => copy.setAttribute(name, value));
  copy.append(...Array.from(el.childNodes));
  return copy;
};

/**
 * The tag a line takes: the slot block's, except that a heading of the
 * content stays a heading (a list item always stays a list item).
 */
const lineTag = (host: HTMLElement, unit: HTMLElement | null): string =>
  tagOf(host) !== 'LI' && unit && HEADING.test(tagOf(unit)) ? tagOf(unit) : tagOf(host);

/**
 * One block in place of the slot block: the slot block's tag and attributes,
 * with the content paragraph's own on top. `tag` overrides the tag.
 */
const makeLineBlock = (host: HTMLElement, unit: HTMLElement | null, tag?: string): HTMLElement => {
  const block = host.ownerDocument.createElement((tag || lineTag(host, unit)).toLowerCase());
  Array.from(host.attributes).forEach(({ name, value }) => {
    if (name !== 'id') block.setAttribute(name, value);
  });
  if (unit) adoptUnitAttributes(block, unit);
  if (tagOf(block) === 'LI') markRtlItem(block);
  return block;
};

/**
 * A content paragraph's own attributes, on the block standing in its place.
 * Where the paragraph aligns or directs itself, the template's own setting
 * (inline or as a Quill class) gives way entirely — the editor draws the
 * paragraph by its own, so the screen does too.
 */
const adoptUnitAttributes = (block: HTMLElement, unit: HTMLElement): void => {
  if (hasOwnAlign(unit)) {
    removeClasses(block, ALIGN_CLASS);
    block.style.removeProperty('text-align');
  }
  if (hasOwnDirection(unit)) {
    removeClasses(block, DIRECTION_CLASS);
    block.removeAttribute('dir');
    block.style.removeProperty('direction');
  }
  if (classNames(unit).some((name) => INDENT_CLASS.test(name))) removeClasses(block, INDENT_CLASS);

  Array.from(unit.attributes).forEach(({ name, value }) => {
    if (name === 'id') return;
    if (name === 'class') {
      classNames(unit).forEach((className) => block.classList.add(className));
    } else if (name === 'style') {
      for (let index = 0; index < unit.style.length; index += 1) {
        const property = unit.style[index];
        block.style.setProperty(property, unit.style.getPropertyValue(property), unit.style.getPropertyPriority(property));
      }
    } else {
      block.setAttribute(name, value);
    }
  });
  dropEmptyAttributes(block);
};

/* ------------------------------ filling a slot ------------------------------ */

interface SlotPlace {
  /** The line the slot is in, or null when it sits in a container. */
  host: HTMLElement | null;
  /** The block the slot is in, of any kind — the line, or the container. */
  block: HTMLElement | null;
  /** The formatting elements between that block and the slot, outermost first. */
  chain: Array<Element>;
}

const slotPlace = (marker: Node, root: Element): SlotPlace => {
  const chain: Array<Element> = [];
  let block: HTMLElement | null = null;
  for (let el = marker.parentElement; el && el !== root; el = el.parentElement) {
    if (isBlock(el)) {
      block = el;
      break;
    }
    chain.unshift(el);
  }
  return { host: block && LINE_HOST.test(tagOf(block)) ? block : null, block, chain };
};

/** The next slot in the document — a text node of its own — and its name. */
const takeMarker = (root: Element): { marker: Text; name: string } | null => {
  const walker = root.ownerDocument.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
  for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
    const match = MARKER_PATTERN.exec(node.data);
    if (!match) continue;
    // Script and style text is never a place for content.
    if (/^(SCRIPT|STYLE|TEXTAREA|TITLE)$/.test(node.parentElement ? tagOf(node.parentElement) : '')) continue;
    const marker = match.index > 0 ? node.splitText(match.index) : node;
    if (marker.data.length > match[0].length) marker.splitText(match[0].length);
    return { marker, name: match[1] };
  }
  return null;
};

const parseContent = (doc: Document, html: string): HTMLElement => {
  // Parsed as the display parses it: inside a <div>, through innerHTML.
  const holder = doc.createElement('div');
  holder.innerHTML = html;
  return holder;
};

/**
 * The slot is in a line (<p>, a heading, a list item): the line is split
 * there, and each paragraph of the content becomes a line of its own. The
 * first and last join the template's text before and after the slot, as
 * pasted paragraphs join the line they land in.
 */
const fillLineSlot = (host: HTMLElement, marker: Text, chain: Array<Element>, units: Array<Unit>): void => {
  if (!units.length) {
    marker.remove();
    return;
  }

  const range = host.ownerDocument.createRange();
  range.setStart(host, 0);
  range.setEndBefore(marker);
  const before = range.cloneContents();
  range.setStartAfter(marker);
  range.setEnd(host, host.childNodes.length);
  const after = range.cloneContents();

  const blocks: Array<Node> = [];
  let open: HTMLElement | null = null;
  let lastWasLine = false;
  const afterHasText = hasSubstance(after);

  if (hasSubstance(before)) {
    open = makeLineBlock(host, null);
    open.append(before);
  }

  units.forEach((unit, index) => {
    if (unit.kind === 'line') {
      // An empty line holds a break, so it keeps its height whatever it is —
      // unless it is the last and the template's text after the slot joins
      // it: that text is the line then, and a break would push it down one.
      const isEmpty = !hasSubstance(unit.nodes);
      const joinsAfter = index === units.length - 1 && afterHasText;
      const text = !isEmpty ? unit.nodes : joinsAfter ? [] : [host.ownerDocument.createElement('br')];
      const line = wrapInChain(text, chain);
      if (index === 0 && open) {
        // Joining the template's text before the slot, the paragraph still
        // brings its own alignment, direction and heading — as the editor
        // draws it.
        if (unit.el) {
          const tag = lineTag(host, unit.el);
          if (tag !== tagOf(open)) open = retag(open, tag);
          adoptUnitAttributes(open, unit.el);
        }
        open.append(...line);
      } else {
        if (open) blocks.push(open);
        open = makeLineBlock(host, unit.el);
        open.append(...line);
      }
      lastWasLine = true;
      return;
    }

    if (open) blocks.push(open);
    open = null;
    lastWasLine = false;
    if (unit.kind === 'tree') {
      styleTree(unit.el, host, chain);
      blocks.push(unit.el);
    } else if (MEDIA_TAGS.has(tagOf(unit.node))) {
      // An image (or video) stands in a line of its own, placed where the
      // template's line would place it — its alignment, its direction. The
      // text formatting has nothing to do with it, nor a heading's size and
      // spacing: a paragraph holds it. In a list of items it stands between
      // them, as the content's other blocks do: not an item, not numbered.
      const holder = makeLineBlock(host, null, 'P');
      holder.classList.remove('ql-direction-rtl');
      holder.appendChild(unit.node);
      blocks.push(holder);
    } else {
      blocks.push(unit.node);
    }
  });

  if (hasSubstance(after)) {
    if (!(open && lastWasLine)) {
      if (open) blocks.push(open);
      open = makeLineBlock(host, null);
    }
    open.append(after);
  }
  if (open) blocks.push(open);

  // A slot in a list item's first paragraph: the display draws that paragraph
  // inline beside the item's marker (Display.vue: `li > p:first-child`), so
  // the item sets where the first line sits — it takes that line's
  // alignment and direction, as the editor shows them.
  const item = host.parentElement;
  const hostWasFirst = Boolean(item && tagOf(item) === 'LI' && item.firstElementChild === host);
  host.replaceWith(...blocks);
  const first = item ? item.firstElementChild : null;
  if (item && hostWasFirst && isElement(first) && tagOf(first) === 'P') liftToItem(item, first);
};

/**
 * The slot is in a container — a division, a quote, a table cell, or no block
 * at all. The content's paragraphs can stand there as they are, so only the
 * formatting around the slot is split to make room for them.
 */
const fillContainerSlot = (marker: Text, chain: Array<Element>, units: Array<Unit>): void => {
  const top: ChildNode = (chain[0] as ChildNode | undefined) || marker;
  const parts: Array<Node> = [];

  if (chain.length) {
    const range = marker.ownerDocument.createRange();
    range.setStartBefore(top);
    range.setEndBefore(marker);
    const before = range.cloneContents();
    if (hasSubstance(before)) parts.push(before);
  }

  units.forEach((unit) => {
    if (unit.kind === 'line') {
      if (unit.el) {
        fillLine(unit.el, chain);
        parts.push(unit.el);
      } else {
        parts.push(...wrapInChain(unit.nodes, chain));
      }
    } else if (unit.kind === 'tree') {
      styleTree(unit.el, null, chain);
      parts.push(unit.el);
    } else {
      parts.push(unit.node);
    }
  });

  if (chain.length) {
    const range = marker.ownerDocument.createRange();
    range.setStartAfter(marker);
    range.setEndAfter(top);
    const after = range.cloneContents();
    if (hasSubstance(after)) parts.push(after);
  }

  top.replaceWith(...parts);
};

const parseTemplate = (html: string): Document =>
  // A doctype, so the template parses in standards mode like the display page.
  new DOMParser().parseFromString(`<!DOCTYPE html><html><body>${html}</body></html>`, 'text/html');

const canParse = (): boolean => typeof DOMParser !== 'undefined';

// The display renders every item on every tick: a filled template is kept, so
// the same one is not parsed again.
const FILLED = new Map<string, string>();
const FILLED_LIMIT = 200;

const remember = (key: string, html: string): string => {
  if (FILLED.size >= FILLED_LIMIT) {
    const oldest = FILLED.keys().next().value;
    if (oldest !== undefined) FILLED.delete(oldest);
  }
  FILLED.set(key, html);
  return html;
};

/**
 * The template with each named slot (richSlotMarker(name) in its text) holding
 * that name's content; a slot with no content given is left empty. A template
 * without slots comes back exactly as given.
 */
export const fillRichSlots = (
  html: string | null | undefined,
  contents: Record<string, string | null | undefined>
): string => {
  const source = String(html ?? '');
  if (!MARKER_PATTERN.test(source)) return source;
  const slots: Record<string, string> = {};
  Object.keys(contents || {}).forEach((name) => {
    slots[name] = withoutSlotMarker(contents[name]);
  });
  const contentOf = (name: string): string => (Object.prototype.hasOwnProperty.call(slots, name) ? slots[name] : '');
  // No DOM to build with (a server render): the plain substitution.
  if (!canParse()) return source.replace(EVERY_MARKER, (marker) => contentOf(marker.slice(1, -1)));

  const key = JSON.stringify([source, slots]);
  const cached = FILLED.get(key);
  if (cached !== undefined) return cached;

  try {
    const doc = parseTemplate(source);
    const root = doc.body;
    for (let found = takeMarker(root); found; found = takeMarker(root)) {
      const { host, chain } = slotPlace(found.marker, root);
      const units = collectUnits(parseContent(doc, contentOf(found.name)));
      if (host) {
        fillLineSlot(host, found.marker, chain, units);
      } else {
        fillContainerSlot(found.marker, chain, units);
      }
    }
    // A marker left where content cannot go (an attribute, a <style>) is dropped.
    return remember(key, root.innerHTML.replace(EVERY_MARKER, '').replace(MARKER_CHARACTERS, ''));
  } catch (error) {
    // This runs inside the display's render: whatever goes wrong, the screen
    // shows the plain substitution rather than nothing at all.
    // eslint-disable-next-line no-console
    console.error('Template slot could not be filled; showing it unstyled.', error);
    return remember(key, source.replace(EVERY_MARKER, (marker) => contentOf(marker.slice(1, -1))));
  }
};

/** The template with the content in its one slot (RICH_SLOT_MARKER). */
export const fillRichSlot = (html: string | null | undefined, contentHtml: string | null | undefined): string =>
  fillRichSlots(html, { slot: contentHtml });

/* ------------------------------ reading a slot ------------------------------ */

// Quill's own size scale (quill.snow.css).
const QUILL_SIZES: StyleMap = { small: '0.75em', large: '1.5em', huge: '2.5em' };

// What an element does to text by being that element, as the display page
// draws it: the browser's defaults, under Bootstrap 4.4.1's reboot, which
// public/index.html loads for every page — the display included.
const BOOTSTRAP_MONOSPACE = 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';
const TAG_STYLES: Record<string, StyleMap> = {
  // Relative, as Bootstrap has it: bold inside bold is bolder still.
  B: { 'font-weight': 'bolder' },
  STRONG: { 'font-weight': 'bolder' },
  I: { 'font-style': 'italic' },
  EM: { 'font-style': 'italic' },
  CITE: { 'font-style': 'italic' },
  VAR: { 'font-style': 'italic' },
  DFN: { 'font-style': 'italic' },
  U: { 'text-decoration-line': 'underline' },
  INS: { 'text-decoration-line': 'underline' },
  S: { 'text-decoration-line': 'line-through' },
  STRIKE: { 'text-decoration-line': 'line-through' },
  DEL: { 'text-decoration-line': 'line-through' },
  // Bootstrap's background; the browser's black text.
  MARK: { 'background-color': '#fcf8e3', color: 'black' },
  SUB: { position: 'relative', bottom: '-0.25em', 'vertical-align': 'baseline', 'font-size': '75%', 'line-height': '0' },
  SUP: { position: 'relative', top: '-0.5em', 'vertical-align': 'baseline', 'font-size': '75%', 'line-height': '0' },
  SMALL: { 'font-size': '80%', 'font-weight': '400' },
  BIG: { 'font-size': 'larger' },
  CODE: { 'font-family': BOOTSTRAP_MONOSPACE, 'font-size': '87.5%', color: '#e83e8c' },
  KBD: { 'font-family': BOOTSTRAP_MONOSPACE },
  SAMP: { 'font-family': BOOTSTRAP_MONOSPACE },
  TT: { 'font-family': 'monospace' },
};

/**
 * A heading as the display page draws it — Bootstrap 4.4.1's sizes, weight
 * and line height (Quill's own heading sizes apply only under .ql-snow, which
 * the display is not). What a paragraph becomes in a heading's place.
 */
export const HEADING_STYLES: Record<string, StyleMap> = {
  h1: { 'font-size': '2.5rem', 'font-weight': '500', 'line-height': '1.2' },
  h2: { 'font-size': '2rem', 'font-weight': '500', 'line-height': '1.2' },
  h3: { 'font-size': '1.75rem', 'font-weight': '500', 'line-height': '1.2' },
  h4: { 'font-size': '1.5rem', 'font-weight': '500', 'line-height': '1.2' },
  h5: { 'font-size': '1.25rem', 'font-weight': '500', 'line-height': '1.2' },
  h6: { 'font-size': '1rem', 'font-weight': '500', 'line-height': '1.2' },
};

// What of the formatting around the slot reaches the text in it. Box
// properties (padding, borders) would repeat on every run of text, so they
// stay out; the offset of sub- and superscript stays in.
const TEXT_PROPERTIES = /^(font|color$|background-color$|text-decoration|text-transform$|text-shadow$|letter-spacing$|word-spacing$|vertical-align$|line-height$|text-emphasis|-webkit-text-stroke|-webkit-text-fill-color$|position$|top$|bottom$)/;
// What a block hands down to the blocks inside it.
const INHERITED_PROPERTIES = /^(font|color$|line-height$|text-align$|text-indent$|text-transform$|text-shadow$|letter-spacing$|word-spacing$|white-space$|text-decoration|background-color$)/;

const readInlineStyle = (el: Element): StyleMap => {
  const style = (el as HTMLElement).style;
  const map: StyleMap = {};
  if (!style) return map;
  for (let index = 0; index < style.length; index += 1) {
    const property = style[index];
    const value = style.getPropertyValue(property).trim();
    if (value && value !== 'initial') map[property] = value;
  }
  return map;
};

/** Quill writes its formats as classes; these are the styles they stand for. */
const quillClassStyle = (el: Element): StyleMap => {
  const map: StyleMap = {};
  const rtl = el.classList.contains('ql-direction-rtl');
  classNames(el).forEach((name) => {
    let match: RegExpExecArray | null;
    if ((match = /^ql-font-(.+)$/.exec(name))) {
      map['font-family'] = `"${match[1]}"`;
    } else if ((match = /^ql-size-(small|large|huge)$/.exec(name))) {
      map['font-size'] = QUILL_SIZES[match[1]];
    } else if ((match = /^ql-align-(center|right|justify|left)$/.exec(name))) {
      map['text-align'] = match[1];
    } else if ((match = /^ql-indent-(\d+)$/.exec(name))) {
      map[rtl ? 'padding-right' : 'padding-left'] = `${Number(match[1]) * 3}em`;
    }
  });
  return map;
};

const elementStyle = (el: Element): StyleMap => ({
  ...(TAG_STYLES[tagOf(el)] || {}),
  ...quillClassStyle(el),
  ...readInlineStyle(el),
});

const parseLength = (value: string): { amount: number; unit: string } | null => {
  const match = /^(-?\d*\.?\d+)(px|pt|em|rem|%)$/.exec(String(value).trim());
  return match ? { amount: Number(match[1]), unit: match[2] } : null;
};

const round = (value: number): number => Math.round(value * 1000) / 1000;

/**
 * An inner font size on top of an outer one: an absolute size replaces it, a
 * relative one scales it — so nested sizes add up as they do on the page.
 */
export const combineFontSize = (outer: string | undefined, inner: string): string => {
  if (!outer) return inner;
  const relative = parseLength(inner);
  let factor: number | null = null;
  if (relative && (relative.unit === 'em' || relative.unit === '%')) {
    factor = relative.unit === '%' ? relative.amount / 100 : relative.amount;
  } else if (inner === 'smaller') {
    factor = 1 / 1.2;
  } else if (inner === 'larger') {
    factor = 1.2;
  }
  if (factor === null) return inner;

  const base = parseLength(outer);
  if (!base) return inner;
  if (base.unit === '%') return `${round((base.amount / 100) * factor)}em`;
  return `${round(base.amount * factor)}${base.unit}`;
};

const WEIGHT_KEYWORDS: Record<string, number> = { normal: 400, bold: 700 };

/** CSS's `bolder`, from a weight. */
export const bolderWeight = (weight: number): number => (weight < 350 ? 400 : weight < 550 ? 700 : weight < 900 ? 900 : weight);

const lighterWeight = (weight: number): number => (weight < 550 ? 100 : weight < 750 ? 400 : 700);

/**
 * A font weight as a number, on top of the weight it sits in: `bolder` and
 * `lighter` step from that one, anything else stands alone.
 */
export const resolveFontWeight = (value: string | undefined, base: number): number => {
  const text = String(value || '').trim();
  if (text === 'bolder') return bolderWeight(base);
  if (text === 'lighter') return lighterWeight(base);
  if (WEIGHT_KEYWORDS[text] !== undefined) return WEIGHT_KEYWORDS[text];
  const number = Number(text);
  return Number.isFinite(number) && number > 0 ? number : base;
};

// An inner weight on an outer one. Relative steps with nothing absolute
// beneath them stay relative — they step from wherever the text is drawn.
const combineFontWeight = (outer: string | undefined, inner: string): string => {
  if (inner !== 'bolder' && inner !== 'lighter') return String(resolveFontWeight(inner, 400));
  if (!outer || outer === 'bolder' || outer === 'lighter') return inner;
  return String(resolveFontWeight(inner, resolveFontWeight(outer, 400)));
};

const combineDecoration = (outer: string | undefined, inner: string): string => {
  const lines = new Set(String(outer || '').split(/\s+/).filter((line) => line && line !== 'none'));
  String(inner).split(/\s+/).forEach((line) => {
    if (line && line !== 'none') lines.add(line);
  });
  return Array.from(lines).join(' ');
};

/** The styles of nested elements, outermost first, as the innermost text sees them. */
const foldStyles = (maps: Array<StyleMap>, accept: RegExp): StyleMap => {
  const folded: StyleMap = {};
  maps.forEach((map) => {
    Object.keys(map).forEach((property) => {
      if (!accept.test(property)) return;
      const value = map[property];
      if (value === 'inherit' || value === 'unset') return;
      if (property === 'font-size') {
        folded[property] = combineFontSize(folded[property], value);
      } else if (property === 'font-weight') {
        folded[property] = combineFontWeight(folded[property], value);
      } else if (property === 'text-decoration-line') {
        const lines = combineDecoration(folded[property], value);
        if (lines) folded[property] = lines;
      } else {
        folded[property] = value;
      }
    });
  });
  return folded;
};

const directionOf = (el: Element): string | null =>
  el.getAttribute('dir') ||
  (el.classList.contains('ql-direction-rtl') ? 'rtl' : null) ||
  (el as HTMLElement).style?.direction ||
  null;

/**
 * The formatting a template gives the named slot — its first, when it has the
 * slot more than once — or null when it has none.
 */
export const readRichSlotFormat = (html: string | null | undefined, name = 'slot'): RichSlotFormat | null => {
  const source = String(html ?? '');
  if (!source.includes(richSlotMarker(name)) || !canParse()) return null;
  try {
    return readSlotFormat(source, name);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Template slot could not be read; the editor shows it unstyled.', error);
    return null;
  }
};

const readSlotFormat = (source: string, name: string): RichSlotFormat | null => {
  const doc = parseTemplate(source);
  const root = doc.body;
  let found = takeMarker(root);
  // Other slots are passed over: emptied, so they are not found again.
  while (found && found.name !== name) {
    found.marker.data = '';
    found = takeMarker(root);
  }
  if (!found) return null;
  const { host, block, chain } = slotPlace(found.marker, root);

  const blockStyle: StyleMap = host ? { ...quillClassStyle(host), ...readInlineStyle(host) } : {};
  const blockDir = host ? directionOf(host) : null;
  delete blockStyle.direction;

  // The blocks further out, outermost first. For a slot in a container, the
  // container itself is one of them: the content sits inside it.
  const outer: Array<Element> = [];
  for (let el = host ? host.parentElement : block; el && el !== root; el = el.parentElement) outer.unshift(el);

  const inheritedDir = outer.reduce<string | null>((dir, el) => directionOf(el) || dir, null);
  const inheritedStyle = foldStyles(outer.map(elementStyle), INHERITED_PROPERTIES);
  delete inheritedStyle.direction;

  return {
    hostTag: host ? tagOf(host).toLowerCase() : null,
    blockStyle,
    blockDir,
    inlineStyle: foldStyles(chain.map(elementStyle), TEXT_PROPERTIES),
    inheritedStyle,
    inheritedDir,
    list: slotList(host),
  };
};

/** The list around a slot in a list item (or in a paragraph inside one). */
const slotList = (host: HTMLElement | null): RichSlotList | null => {
  if (!host) return null;
  const itemPerLine = tagOf(host) === 'LI';
  const item = itemPerLine ? host : host.parentElement;
  const list = item ? item.parentElement : null;
  if (!item || tagOf(item) !== 'LI' || !list || !/^(UL|OL)$/.test(tagOf(list))) return null;
  const items = Array.from(list.children).filter((child) => tagOf(child) === 'LI');
  return {
    ordered: tagOf(list) === 'OL',
    itemPerLine,
    marker: itemPerLine || item.firstElementChild === host,
    start: items.indexOf(item) + 1,
    rtl: item.classList.contains('ql-direction-rtl'),
  };
};
